/**
 * Générateur — Effectuer des conversions d'aire
 * Attendu ID : 644 | Niveau : 6ème | Domaine : Grandeurs et mesures
 *
 * Types générés : qcm_unique, texte, relier, texte_trou_saisie
 * Labels de référence :
 *   - Compléter un tableau de conversion d'aire
 *   - Effectuer une conversion directe entre unités d'aire
 * Variantes estimées : 1 500+
 */

import {
    randomChoice, randInt,
    makeQcm, makeSaisie, makeTexteTrou, makeRelier,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 644;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const PRENOMS = ['Adam', 'Amélie', 'Chloé', 'Hugo', 'Inès', 'Jade', 'Karim', 'Léo', 'Lucas', 'Manon', 'Sofia', 'Thomas', 'Yanis'];

// ─── Unités et facteurs ───
// Unités consécutives : mm² → cm² → dm² → m²  (facteur ×100 à chaque étape)
const UNITS = ['mm²', 'cm²', 'dm²', 'm²'];

// Paires de conversion autorisées (source_index, target_index) avec facteur
// Consécutives : facteur 100
// Non-consécutives (2 étapes) : facteur 10 000
const PAIRS = [
    { from: 0, to: 1, factor: 100 },   // mm² → cm²
    { from: 1, to: 0, factor: 100 },   // cm² → mm²
    { from: 1, to: 2, factor: 100 },   // cm² → dm²
    { from: 2, to: 1, factor: 100 },   // dm² → cm²
    { from: 2, to: 3, factor: 100 },   // dm² → m²
    { from: 3, to: 2, factor: 100 },   // m²  → dm²
    { from: 0, to: 2, factor: 10000 }, // mm² → dm²
    { from: 2, to: 0, factor: 10000 }, // dm² → mm²
    { from: 1, to: 3, factor: 10000 }, // cm² → m²
    { from: 3, to: 1, factor: 10000 }, // m²  → cm²
];

// ─── Helpers ───

/** Génère une valeur de conversion adaptée */
const genValue = (pair) => {
    const goingUp = pair.to > pair.from; // vers unité plus grande → on divise → valeur grande
    if (goingUp) {
        // On divise, donc on veut des valeurs assez grandes pour avoir un résultat lisible
        const pool = [
            randInt(1, 99) * 100,           // entier multiple de 100 : 100, 200, ..., 9900
            randInt(1, 999),                 // entier quelconque
            randInt(1, 99) * 10 + randInt(1, 9) * 0.1, // décimal 1 chiffre
        ];
        return randomChoice(pool);
    } else {
        // On multiplie → valeurs plus petites
        const pool = [
            randInt(1, 50),                  // petit entier
            +(randInt(1, 99) / 10).toFixed(1), // décimal 1 chiffre après virgule
            +(randInt(1, 999) / 100).toFixed(2), // décimal 2 chiffres
        ];
        return randomChoice(pool);
    }
};

/** Formate un nombre pour l'affichage français (virgule, espaces milliers) */
const fmt = (n) => {
    const s = String(n).replace('.', ',');
    // Ajout d'espaces pour les milliers (partie entière seulement)
    const parts = s.split(',');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join(',');
};

/** Calcule le résultat de conversion */
const convert = (value, pair) => {
    const goingUp = pair.to > pair.from;
    if (goingUp) {
        return value / pair.factor;
    } else {
        return value * pair.factor;
    }
};

/** Arrondit proprement pour éviter les flottants */
const clean = (n) => {
    return +(Math.round(n * 1e8) / 1e8);
};

/** Génère des distracteurs pour une conversion */
const makeDistractors = (correctVal, value, pair) => {
    const goingUp = pair.to > pair.from;
    const distractors = new Set();

    // Erreur 1 : facteur ×10 au lieu de ×100 (consécutif) ou ×100 au lieu de ×10 000 (non consécutif)
    if (goingUp) {
        distractors.add(clean(value / (pair.factor === 100 ? 10 : 100)));
    } else {
        distractors.add(clean(value * (pair.factor === 100 ? 10 : 100)));
    }

    // Erreur 2 : sens inversé
    if (goingUp) {
        distractors.add(clean(value * pair.factor));
    } else {
        distractors.add(clean(value / pair.factor));
    }

    // Erreur 3 : facteur ×1000
    if (goingUp) {
        distractors.add(clean(value / 1000));
    } else {
        distractors.add(clean(value * 1000));
    }

    // Erreur 4 : un seul rang de virgule (décalage ×10 dans la mauvaise direction)
    if (goingUp) {
        distractors.add(clean(value / 10));
    } else {
        distractors.add(clean(value * 10));
    }

    // Supprimer la bonne réponse et les zéros/négatifs
    distractors.delete(clean(correctVal));
    distractors.delete(0);

    const arr = [...distractors].filter(d => d > 0);
    // Prendre 3 distracteurs, compléter si nécessaire
    while (arr.length < 3) {
        const offset = randInt(1, 5) * (goingUp ? 0.01 : 10);
        const d = clean(correctVal + offset);
        if (d !== clean(correctVal) && d > 0 && !arr.includes(d)) arr.push(d);
    }
    return arr.slice(0, 3);
};

// ─── Labels ───
const L_TABLEAU = 'Compléter un tableau de conversion d\'aire';
const L_DIRECTE = 'Effectuer une conversion directe entre unités d\'aire';

// ─── Générateurs ───

// === Exercice 1 : Compléter un tableau de conversion d'aire ===

const genTableauQcm = () => {
    const pair = randomChoice(PAIRS);
    const value = genValue(pair);
    const result = clean(convert(value, pair));
    const uFrom = UNITS[pair.from];
    const uTo = UNITS[pair.to];
    const prenom = randomChoice(PRENOMS);

    const distractors = makeDistractors(result, value, pair);
    const choices = [result, ...distractors].map(v => `${fmt(v)} ${uTo}`);

    const texte = `<p>${prenom} doit convertir ${fmt(value)} ${uFrom} en ${uTo}. Quel est le résultat ?</p>`;
    const payload = makeQcm(choices, choices[0], texte, opts(L_TABLEAU));
    payload._meta.generatorKey = 'conversion_tableau';
    return payload;
};

const genTableauTexte = () => {
    const pair = randomChoice(PAIRS);
    const value = genValue(pair);
    const result = clean(convert(value, pair));
    const uFrom = UNITS[pair.from];
    const uTo = UNITS[pair.to];

    const texte = `<p>Convertis ${fmt(value)} ${uFrom} en ${uTo}.</p>`;
    const payload = makeSaisie(
        String(fmt(result)),
        texte,
        // strict RETIRÉ (revue 2026-07-31) : la compétence est la CONVERSION,
        // pas l'écriture — la tolérance par valeur accepte 4200 pour « 4 200 ».
        { ...opts(L_TABLEAU), suffixe: uTo }
    );
    payload._meta.generatorKey = 'conversion_tableau';
    return payload;
};

const genTableauRelier = () => {
    // Refonte bilan prof D3 (2026-07-27) : 3 sources / 6 cibles, paires
    // d'unités VARIÉES (l'ancienne version convertissait tout par ×10 000 →
    // appariement par tri croissant), consigne sans unités, et UN leurre par
    // source : la conversion au facteur des LONGUEURS (×10 par étape au lieu
    // de ×100 — code facteur_longueur ; repli sens_inverse). Garde
    // d'exclusivité : toutes les mesures du tirage (sources, cibles, leurres)
    // sont deux à deux distinctes en valeur absolue (comparaison en mm²).
    const baseMm2 = (value, unitIdx) => clean(value * Math.pow(100, unitIdx));

    const chosen = [];
    let attempts = 0;
    while (chosen.length < 3 && attempts < 200) {
        attempts++;
        const pair = randomChoice(PAIRS);
        const v = genValue(pair);
        const r = clean(convert(v, pair));
        if (r <= 0) continue;
        const base = baseMm2(v, pair.from);
        if (chosen.some(c => Math.abs(baseMm2(c.v, c.pair.from) - base) < 1e-9)) continue;
        chosen.push({ pair, v, r });
    }
    if (chosen.length < 3) {
        chosen.length = 0;
        chosen.push({ pair: PAIRS[1], v: 7, r: 700 });      // 7 cm² → 700 mm²
        chosen.push({ pair: PAIRS[5], v: 6, r: 600 });      // 6 m² → 600 dm²
        chosen.push({ pair: PAIRS[3], v: 42, r: 4200 });    // 42 dm² → 4 200 cm²
    }

    const pairs = chosen.map(c => ({
        source: `${fmt(c.v)} ${UNITS[c.pair.from]}`,
        target: `${fmt(c.r)} ${UNITS[c.pair.to]}`,
    }));

    const prisStr = new Set(pairs.map(p => p.target));
    const basesPrises = chosen.map(c => baseMm2(c.v, c.pair.from));
    const leurres = [];
    for (const c of chosen) {
        const steps = Math.abs(c.pair.to - c.pair.from);
        const goingUp = c.pair.to > c.pair.from;
        const candidats = [
            // ×10 par étape (facteur des longueurs) au lieu de ×100
            { valeur: clean(goingUp ? c.v / Math.pow(10, steps) : c.v * Math.pow(10, steps)), code: 'facteur_longueur' },
            // sens de conversion inversé
            { valeur: clean(goingUp ? c.v * c.pair.factor : c.v / c.pair.factor), code: 'sens_inverse' },
            // facteur ×1000
            { valeur: clean(goingUp ? c.v / 1000 : c.v * 1000), code: 'facteur_1000' },
        ];
        for (const cand of candidats) {
            if (cand.valeur <= 0) continue;
            const txt = `${fmt(cand.valeur)} ${UNITS[c.pair.to]}`;
            if (prisStr.has(txt)) continue;
            // Exclusivité : le leurre ne doit être la conversion CORRECTE
            // d'aucune source du tirage (mesure égale → lien défendable)
            const candBase = baseMm2(cand.valeur, c.pair.to);
            if (basesPrises.some(b => Math.abs(b - candBase) < 1e-9)) continue;
            prisStr.add(txt);
            leurres.push({ target: txt, code: cand.code });
            break;
        }
    }

    const texte = '<p>Relie chaque mesure de gauche à la mesure égale à droite. '
        + 'Attention : certaines étiquettes ne correspondent à aucune mesure.</p>';
    const payload = makeRelier(pairs, texte, { ...opts(L_TABLEAU), cibles_leurres: leurres });
    payload._meta.generatorKey = 'conversion_tableau';
    payload._meta.params = {
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

// === Exercice 2 : Effectuer une conversion directe entre unités d'aire ===

const genDirecteQcm = () => {
    const pair = randomChoice(PAIRS);
    const value = genValue(pair);
    const result = clean(convert(value, pair));
    const uFrom = UNITS[pair.from];
    const uTo = UNITS[pair.to];

    const distractors = makeDistractors(result, value, pair);
    const choices = [result, ...distractors].map(v => `${fmt(v)} ${uTo}`);

    const texte = `<p>Complète : ${fmt(value)} ${uFrom} = … ${uTo}</p>`;
    const payload = makeQcm(choices, choices[0], texte, opts(L_DIRECTE));
    payload._meta.generatorKey = 'conversion_directe';
    return payload;
};

const genDirecteTexte = () => {
    const pair = randomChoice(PAIRS);
    const value = genValue(pair);
    const result = clean(convert(value, pair));
    const uFrom = UNITS[pair.from];
    const uTo = UNITS[pair.to];

    const texte = `<p>Complète : ${fmt(value)} ${uFrom} = … ${uTo}</p>`;
    const payload = makeSaisie(
        String(fmt(result)),
        texte,
        // strict RETIRÉ (revue 2026-07-31) : même décision que la saisie tableau.
        { ...opts(L_DIRECTE), suffixe: uTo }
    );
    payload._meta.generatorKey = 'conversion_directe';
    return payload;
};

const genDirecteTexteTrou = () => {
    // On choisit une paire consécutive pour le raisonnement étape par étape
    const consecutivePairs = PAIRS.filter(p => p.factor === 100);
    const pair = randomChoice(consecutivePairs);
    const value = genValue(pair);
    const result = clean(convert(value, pair));
    const uFrom = UNITS[pair.from];
    const uTo = UNITS[pair.to];
    const goingUp = pair.to > pair.from;

    let phrase;
    if (goingUp) {
        // Vers plus grand : on divise
        phrase = `1 ${uTo} = {{100}} ${uFrom}. Donc pour passer de ${uFrom} à ${uTo}, on divise par {{100}}. Ainsi ${fmt(value)} ${uFrom} = ${fmt(value)} ÷ {{100}} = {{${fmt(result)}}} ${uTo}.`;
    } else {
        // Vers plus petit : on multiplie
        phrase = `1 ${uFrom} = {{100}} ${uTo}. Donc pour passer de ${uFrom} à ${uTo}, on multiplie par {{100}}. Ainsi ${fmt(value)} ${uFrom} = ${fmt(value)} × {{100}} = {{${fmt(result)}}} ${uTo}.`;
    }

    const prenom = randomChoice(PRENOMS);
    const texte = `<p>${prenom} doit convertir ${fmt(value)} ${uFrom} en ${uTo}. Complète le raisonnement.</p>`;

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_DIRECTE), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'conversion_directe';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    conversion_tableau_qcm_unique:       genTableauQcm,
    conversion_tableau_texte:            genTableauTexte,
    conversion_tableau_relier:           genTableauRelier,
    conversion_directe_qcm_unique:      genDirecteQcm,
    conversion_directe_texte:           genDirecteTexte,
    conversion_directe_texte_trou_saisie: genDirecteTexteTrou,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'conversion_tableau', label: L_TABLEAU, formatsList: buildFormats('conversion_tableau') },
    { id: 'conversion_directe', label: L_DIRECTE, formatsList: buildFormats('conversion_directe') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
